import {useEffect, useMemo, useState} from 'react';
import {Box, Table, TableBody, TableCell, TableRow, Typography, type SxProps} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {MoneyField} from '@/components/common';
import {useFsAdjustStore} from '@/store/audit';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {usePlMultiPeriodChainStore} from '@/store/audit/plMultiPeriodChainStore';
import {useWpPeriodScopeContext} from '@/adjustment/hooks/WpPeriodScopeContext';
import {resolvePriorPeriodSlice} from '@/utils/wpPriorPeriodBfRollForward';
import {lookupPlSliceAudited} from '@/utils/plSliceAuditedLookup';
import {useRegisterWpCheckSummary} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/wpCheckSummaryContext';
import {resolveWpIgnoreLastYear} from '@/utils/auditPeriodWpMulti';

const GAIN_ON_DISPOSAL_CODE = 'PL-OI-GOD';
const LOSS_ON_DISPOSAL_CODE = 'PL-AE-LOD';
const EXCHANGE_GAIN_CODE = 'PL-OI-EXG';
const EXCHANGE_LOSS_CODE = 'PL-AE-EXL';
const TAX_PAYABLE_CODE = 'BS-CL-TAXP';
const TAX_REFUNDABLE_CODE = 'BS-CA-TAXR';

const DIR_CODES = ['BS-CA-DIR', 'BS-CL-DTD'];

/** Resolve FS line fsId from adjust store by code (case-insensitive fallback). */
function resolveFsIdByCode(
    byCode: Record<string, {fsId?: number | string; code?: string} | undefined>,
    getLineByCode: (code: string) => {fsId?: number | string} | null | undefined,
    rawCode: string,
): number {
    const normalized = String(rawCode || '').trim().toUpperCase();
    if (!normalized) return 0;
    const direct = byCode[normalized] || getLineByCode(normalized);
    if (direct?.fsId) return Number(direct.fsId) || 0;
    const hit = Object.values(byCode).find(
        (n) => String(n?.code || '').trim().toUpperCase() === normalized,
    );
    return Number(hit?.fsId) || 0;
}

export interface WpPerCheckPayload {
    perName: string;
    currentValue: number;
    lastYearValue: number;
    sx?: SxProps;
    yoa: string;
    lastYoa: string;
}

export interface WpPerCheckAmountRow {
    key: string;
    name: string;
    currentValue: number;
    /**
     * Last YOA stored value. For Split rows: `null` = follow calculated (display);
     * `0` or any value ≠ calculated = active Split. Non-split rows use number as before.
     */
    lastYearValue: number | null;
    /** Per-row override; falls back to options.lastYearEditable when omitted. */
    lastYearEditable?: boolean;
    /**
     * When set: last YOA uses MoneyField Split (left = calculated, right = override).
     * Display when value is null or equals calculated; Split when value is set and differs (incl. 0).
     */
    lastYearCalculated?: number;
}

export interface WpPerCheckOptions {
    ignoreLastYear?: boolean;
    /** When true, paired gain/loss codes validate against their own perData instead of net difference. */
    separatePairCheck?: boolean;
    /** Override Per {statement} comparison value (e.g. parent FS value excluding AutoAdjust). */
    auditedValueOverride?: number;
    /** Override Per {statement} last-year comparison value (MULTI prior-slice Audited). */
    lastYearAuditedOverride?: number;
    /**
     * When 'variance', Control row shows Variance % instead of absolute difference.
     * Balanced when |variance| <= varianceThreshold (default 5).
     */
    controlDisplay?: 'diff' | 'variance';
    /** Percent threshold for variance control (default 5). */
    varianceThreshold?: number;
    /** Hide Per {statement} and Control rows (notes / amount-only display). Always reports balanced. */
    amountsOnly?: boolean;
    /** Allow editing lastYearValue via MoneyField (ignored when ignoreLastYear). */
    lastYearEditable?: boolean;
    /**
     * Extra / multiple amount rows (e.g. One year + Over a year in one check).
     * When set, these replace the single payload perName amount row.
     * Accordion summary still uses payload.perName.
     */
    amountRows?: WpPerCheckAmountRow[];
    /** Min width for the first (label) column so labels stay on one line. */
    labelColumnMinWidth?: number | string;
    /** Last-YOA amount cells: TableCell + MoneyField vertical padding = 1px (Rental commitment). */
    compactLastYearCell?: boolean;
    /** Amount display: false → no fraction digits (e.g. Rental commitment). Default true. */
    showCents?: boolean;
}

export interface WpPerCheckProps {
    payload: WpPerCheckPayload;
    code: string;
    currentPer?: (balanced: boolean) => void;
    lastYearPer?: (balanced: boolean) => void;
    options?: WpPerCheckOptions;
    /** Called when lastYearValue is edited (requires options.lastYearEditable). */
    onLastYearChange?: (value: number) => void;
    /** Called when an amountRows lastYearValue is edited. */
    onAmountLastYearChange?: (key: string, value: number) => void;
    /** Split cancel: parent should persist calculated last YOA for this amount row key. */
    onAmountLastYearEditCancel?: (key: string) => void;
}

const formatAmount = (value: number, showCents = true) => {
    const n = Number(value) || 0;
    const formatted = Math.abs(n).toLocaleString('en-US', {
        minimumFractionDigits: showCents ? 2 : 0,
        maximumFractionDigits: showCents ? 2 : 0,
    });
    return n < 0 ? `(${formatted})` : formatted;
};

export function WpPerCheck({
    payload,
    code,
    currentPer,
    lastYearPer,
    options,
    onLastYearChange,
    onAmountLastYearChange,
    onAmountLastYearEditCancel,
}: WpPerCheckProps) {
    // ✅ 从 companyAnnualStore 的 currentAnnualId（由 FsAdjustment 初始化）订阅 firstAudit
    const isFirstAudit = useCompanyAnnualStore((s) => {
        const id = s.currentAnnualId;
        return id ? Number(s.byAnnualId[id]?.firstAudit ?? 0) === 1 : false;
    });
    // MULTI PL Tab: Per PL must use slice Audited (not annual perDataByCode)
    const periodScope = useWpPeriodScopeContext();
    // FirstAudit + MULTI: hide last YOA only on first Period Tab (BankLoan hideLastYoa)
    const ignoreLastYear = options?.ignoreLastYear ?? resolveWpIgnoreLastYear(isFirstAudit, periodScope);
    const separatePairCheck = options?.separatePairCheck ?? false;
    const auditedValueOverride = options?.auditedValueOverride;
    const lastYearAuditedOverride = options?.lastYearAuditedOverride;
    const controlDisplay = options?.controlDisplay ?? 'diff';
    const varianceThreshold = options?.varianceThreshold ?? 5;
    const amountsOnly = options?.amountsOnly ?? false;
    const lastYearEditable = (options?.lastYearEditable ?? false) && !ignoreLastYear;
    const amountRows = options?.amountRows;
    const labelColumnMinWidth = options?.labelColumnMinWidth ?? (amountRows?.length ? 110 : undefined);
    const compactLastYearCell = options?.compactLastYearCell ?? false;
    const showCents = options?.showCents ?? true;
    /** showCents=false → compare at whole-unit display; else float epsilon. */
    const lastYearEqualEps = showCents ? 1e-6 : 0.5;
    /** Equal stored/calc + EditIcon: force Split until Cancel or parent clears override. */
    const [forcedLastYearSplitKeys, setForcedLastYearSplitKeys] = useState<Record<string, boolean>>({});
    const normalizedCode = useMemo(() => String(code || '').trim().toUpperCase(), [code]);
    // Special handling for GOD/LOD / EXG/EXL: Per {statement} = primary − secondary (net)
    const isGainLossDisposalCode = !separatePairCheck
        && (normalizedCode === GAIN_ON_DISPOSAL_CODE || normalizedCode === LOSS_ON_DISPOSAL_CODE);
    const isExchangeGainLossCode = !separatePairCheck
        && (normalizedCode === EXCHANGE_GAIN_CODE || normalizedCode === EXCHANGE_LOSS_CODE);
    const isNetPairCode = isGainLossDisposalCode || isExchangeGainLossCode;

    const perData = useFsAdjustStore((s) =>
        normalizedCode ? (s.perDataByCode[normalizedCode] ?? null) : null,
    );
    const auditedBySlice = usePlMultiPeriodChainStore((s) => s.auditedBySlice);
    const periods = useFsAdjustStore((s) => s.adjustRecord?.periods);
    const annualPlLine = useFsAdjustStore((s) => s.adjustRecord?.plLine);
    const lineFsId = useFsAdjustStore((s) => resolveFsIdByCode(s.byCode, s.getLineByCode, normalizedCode));
    // Pair primary/secondary fsIds for MULTI slice net (aligned with annual god−lod / exg−exl)
    const pairPrimaryFsId = useFsAdjustStore((s) => {
        if (isGainLossDisposalCode) return resolveFsIdByCode(s.byCode, s.getLineByCode, GAIN_ON_DISPOSAL_CODE);
        if (isExchangeGainLossCode) return resolveFsIdByCode(s.byCode, s.getLineByCode, EXCHANGE_GAIN_CODE);
        return 0;
    });
    const pairSecondaryFsId = useFsAdjustStore((s) => {
        if (isGainLossDisposalCode) return resolveFsIdByCode(s.byCode, s.getLineByCode, LOSS_ON_DISPOSAL_CODE);
        if (isExchangeGainLossCode) return resolveFsIdByCode(s.byCode, s.getLineByCode, EXCHANGE_LOSS_CODE);
        return 0;
    });
    const isPlMultiScope = Boolean(
        periodScope?.sliceKey
        && (perData?.statement === 'PL' || normalizedCode.startsWith('PL-') || isNetPairCode),
    );

    const multiCurrentAudited = useMemo(() => {
        if (!isPlMultiScope) return null;
        const sliceKey = periodScope?.sliceKey;
        // GOD/LOD & EXG/EXL: net slice Audited (primary − secondary), not single code
        if (isNetPairCode) {
            if (!pairPrimaryFsId && !pairSecondaryFsId) return null;
            const primary = pairPrimaryFsId
                ? lookupPlSliceAudited(auditedBySlice, periods, sliceKey, pairPrimaryFsId, annualPlLine)
                : null;
            const secondary = pairSecondaryFsId
                ? lookupPlSliceAudited(auditedBySlice, periods, sliceKey, pairSecondaryFsId, annualPlLine)
                : null;
            if (primary == null && secondary == null) return null;
            return (primary ?? 0) - (secondary ?? 0);
        }
        if (!lineFsId) return null;
        return lookupPlSliceAudited(
            auditedBySlice,
            periods,
            sliceKey,
            lineFsId,
            annualPlLine,
        );
    }, [
        isPlMultiScope,
        isNetPairCode,
        pairPrimaryFsId,
        pairSecondaryFsId,
        lineFsId,
        auditedBySlice,
        periods,
        periodScope?.sliceKey,
        annualPlLine,
    ]);

    const multiLastYearAudited = useMemo(() => {
        if (!isPlMultiScope) return null;
        const prior = resolvePriorPeriodSlice(periods, periodScope?.sliceKey);
        if (!prior) return null; // first MULTI tab → annual lastYearAudited
        if (isNetPairCode) {
            if (!pairPrimaryFsId && !pairSecondaryFsId) return null;
            const primary = pairPrimaryFsId
                ? lookupPlSliceAudited(auditedBySlice, periods, prior.priorKey, pairPrimaryFsId, annualPlLine)
                : null;
            const secondary = pairSecondaryFsId
                ? lookupPlSliceAudited(auditedBySlice, periods, prior.priorKey, pairSecondaryFsId, annualPlLine)
                : null;
            if (primary == null && secondary == null) return null;
            return (primary ?? 0) - (secondary ?? 0);
        }
        if (!lineFsId) return null;
        return lookupPlSliceAudited(
            auditedBySlice,
            periods,
            prior.priorKey,
            lineFsId,
            annualPlLine,
        );
    }, [
        isPlMultiScope,
        isNetPairCode,
        pairPrimaryFsId,
        pairSecondaryFsId,
        lineFsId,
        auditedBySlice,
        periods,
        periodScope?.sliceKey,
        annualPlLine,
    ]);

    const godPerData = useFsAdjustStore((s) =>
        isGainLossDisposalCode ? (s.perDataByCode[GAIN_ON_DISPOSAL_CODE] ?? null) : null,
    );
    const lodPerData = useFsAdjustStore((s) =>
        isGainLossDisposalCode ? (s.perDataByCode[LOSS_ON_DISPOSAL_CODE] ?? null) : null,
    );

    const exgPerData = useFsAdjustStore((s) =>
        isExchangeGainLossCode ? (s.perDataByCode[EXCHANGE_GAIN_CODE] ?? null) : null,
    );
    const exlPerData = useFsAdjustStore((s) =>
        isExchangeGainLossCode ? (s.perDataByCode[EXCHANGE_LOSS_CODE] ?? null) : null,
    );

    // Tax Payable / Refundable: Per BS = BS-CL-TAXP audited - BS-CA-TAXR audited
    const isTaxPayableRefundableCode = normalizedCode === TAX_PAYABLE_CODE
        || normalizedCode === TAX_REFUNDABLE_CODE;
    const taxpPerData = useFsAdjustStore((s) =>
        isTaxPayableRefundableCode ? (s.perDataByCode[TAX_PAYABLE_CODE] ?? null) : null,
    );
    const taxrPerData = useFsAdjustStore((s) =>
        isTaxPayableRefundableCode ? (s.perDataByCode[TAX_REFUNDABLE_CODE] ?? null) : null,
    );

    const auditedValue = useMemo(() => {
        if (isGainLossDisposalCode) {
            const godAudited = Number(godPerData?.auditedValue ?? 0) || 0;
            const lodAudited = Number(lodPerData?.auditedValue ?? 0) || 0;
            return godAudited - lodAudited;
        }
        if (isExchangeGainLossCode) {
            const exgAudited = Number(exgPerData?.auditedValue ?? 0) || 0;
            const exlAudited = Number(exlPerData?.auditedValue ?? 0) || 0;
            return exgAudited - exlAudited;
        }
        if (isTaxPayableRefundableCode) {
            const taxpAudited = Number(taxpPerData?.auditedValue ?? 0) || 0;
            const taxrAudited = Number(taxrPerData?.auditedValue ?? 0) || 0;
            return taxpAudited - taxrAudited;
        }
        return Number(perData?.auditedValue ?? 0) || 0;
    }, [
        isGainLossDisposalCode,
        godPerData?.auditedValue,
        lodPerData?.auditedValue,
        isExchangeGainLossCode,
        exgPerData?.auditedValue,
        exlPerData?.auditedValue,
        isTaxPayableRefundableCode,
        taxpPerData?.auditedValue,
        taxrPerData?.auditedValue,
        perData?.auditedValue,
    ]);

    const effectiveAuditedValue = useMemo(() => {
        if (auditedValueOverride != null && Number.isFinite(Number(auditedValueOverride))) {
            return Number(auditedValueOverride) || 0;
        }
        // MULTI PL: prefer current-slice Audited over annual perData
        if (multiCurrentAudited != null) {
            return multiCurrentAudited;
        }
        return auditedValue;
    }, [auditedValueOverride, multiCurrentAudited, auditedValue]);

    const lastYearAudited = useMemo(() => {
        if (isGainLossDisposalCode) {
            const godLastYear = Number(godPerData?.lastYearAudited ?? 0) || 0;
            const lodLastYear = Number(lodPerData?.lastYearAudited ?? 0) || 0;
            return godLastYear - lodLastYear;
        }
        if (isExchangeGainLossCode) {
            const exgLastYear = Number(exgPerData?.lastYearAudited ?? 0) || 0;
            const exlLastYear = Number(exlPerData?.lastYearAudited ?? 0) || 0;
            return exgLastYear - exlLastYear;
        }
        if (isTaxPayableRefundableCode) {
            const taxpLastYear = Number(taxpPerData?.lastYearAudited ?? 0) || 0;
            const taxrLastYear = Number(taxrPerData?.lastYearAudited ?? 0) || 0;
            return taxpLastYear - taxrLastYear;
        }
        return Number(perData?.lastYearAudited ?? 0) || 0;
    }, [
        isGainLossDisposalCode,
        godPerData?.lastYearAudited,
        lodPerData?.lastYearAudited,
        isExchangeGainLossCode,
        exgPerData?.lastYearAudited,
        exlPerData?.lastYearAudited,
        isTaxPayableRefundableCode,
        taxpPerData?.lastYearAudited,
        taxrPerData?.lastYearAudited,
        perData?.lastYearAudited,
    ]);

    const effectiveLastYearAudited = useMemo(() => {
        if (lastYearAuditedOverride != null && Number.isFinite(Number(lastYearAuditedOverride))) {
            return Number(lastYearAuditedOverride) || 0;
        }
        // MULTI non-first Tab: prior-slice Audited (e.g. 2025/26 → 2024/25)
        if (multiLastYearAudited != null) {
            return multiLastYearAudited;
        }
        return lastYearAudited;
    }, [lastYearAuditedOverride, multiLastYearAudited, lastYearAudited]);

    // Pair codes are always PL; avoid falling back to "BS" when the dynamic side lacks perData
    const statement = perData?.statement
        ?? (isNetPairCode || normalizedCode.startsWith('PL-') ? 'PL' : 'BS');

    // Dynamic perName based on currentValue for Gain/Loss Disposal and Exchange Gain/Loss
    const displayPerName = useMemo(() => {
        if (separatePairCheck) {
            return payload.perName;
        }
        if (isGainLossDisposalCode) {
            return payload.currentValue >= 0 ? 'Gain on Disposal' : 'Loss on Disposal';
        }
        if (isExchangeGainLossCode) {
            return payload.currentValue >= 0 ? 'Exchange Gain' : 'Exchange Loss';
        }
        return payload.perName;
    }, [separatePairCheck, isGainLossDisposalCode, isExchangeGainLossCode, payload.currentValue, payload.perName]);

    const currentDiff = useMemo(() => (Number(payload.currentValue) || 0) - effectiveAuditedValue, [payload.currentValue, effectiveAuditedValue]);
    const lastYearDiff = useMemo(() => (Number(payload.lastYearValue) || 0) - effectiveLastYearAudited, [payload.lastYearValue, effectiveLastYearAudited]);

    const currentVariancePerc = useMemo(() => {
        const current = Number(payload.currentValue) || 0;
        const per = effectiveAuditedValue;
        if (per === 0) return current === 0 ? 0 : 100;
        return ((current - per) / per) * 100;
    }, [payload.currentValue, effectiveAuditedValue]);

    const lastYearVariancePerc = useMemo(() => {
        const current = Number(payload.lastYearValue) || 0;
        const per = effectiveLastYearAudited;
        if (per === 0) return current === 0 ? 0 : 100;
        return ((current - per) / per) * 100;
    }, [payload.lastYearValue, effectiveLastYearAudited]);

    const currentOk = useMemo(() => {
        if (amountsOnly) return true;
        if (controlDisplay === 'variance') {
            return Math.abs(currentVariancePerc) <= varianceThreshold;
        }
        return Math.abs(currentDiff) <= 0.01;
    }, [amountsOnly, controlDisplay, currentVariancePerc, varianceThreshold, currentDiff]);

    const lastYearOk = useMemo(() => {
        if (amountsOnly) return true;
        if (controlDisplay === 'variance') {
            return Math.abs(lastYearVariancePerc) <= varianceThreshold;
        }
        return Math.abs(lastYearDiff) <= 0.01;
    }, [amountsOnly, controlDisplay, lastYearVariancePerc, varianceThreshold, lastYearDiff]);

    // Control soft band: (0, 1] or [-1, 0) → UnBalancing chip yellow (toolbar)
    const currentControlValue = controlDisplay === 'variance' ? currentVariancePerc : currentDiff;
    const lastYearControlValue = controlDisplay === 'variance' ? lastYearVariancePerc : lastYearDiff;
    const currentSoftUnbalanced = useMemo(
        () => !currentOk && Math.abs(currentControlValue) <= 1,
        [currentOk, currentControlValue],
    );
    const lastYearSoftUnbalanced = useMemo(
        () => !lastYearOk && Math.abs(lastYearControlValue) <= 1,
        [lastYearOk, lastYearControlValue],
    );

    useEffect(() => {
        if (currentPer) currentPer(currentOk);
    }, [currentPer, currentOk]);

    useEffect(() => {
        // If ignoreLastYear is true, always report lastYear as balanced (skip validation)
        if (lastYearPer) lastYearPer(ignoreLastYear ? true : lastYearOk);
    }, [lastYearPer, lastYearOk, ignoreLastYear]);

    useRegisterWpCheckSummary({
        name: displayPerName,
        yoa: payload.yoa,
        lastYoa: payload.lastYoa,
        currentBalanced: currentOk,
        lastYearBalanced: lastYearOk,
        ignoreLastYear,
        currentSoftUnbalanced,
        lastYearSoftUnbalanced,
    });

    const diffCellSx = (ok: boolean) => ({
        color: ok ? 'text.primary' : 'error.main',
        fontWeight: ok ? 500 : 700,
        textAlign: 'center' as const,
        whiteSpace: 'nowrap' as const,
    });

    const amountCellSx = {
        textAlign: 'center',
        fontWeight: 500,
        whiteSpace: 'nowrap',
    } as const;

    const lastYearAmountCellSx = {
        ...amountCellSx,
        ...(compactLastYearCell ? {py: '1px'} : {}),
    } as const;

    /** Override InputField inline paddingTop/Bottom: 4 when compactLastYearCell. */
    const lastYearMoneyFieldSx = compactLastYearCell
        ? {
            '& .MuiOutlinedInput-input': {
                paddingTop: '1px !important',
                paddingBottom: '1px !important',
            },
            '& .MuiInputBase-root': {
                minHeight: 'auto',
            },
        }
        : undefined;

    const labelCellSx = {
        whiteSpace: 'nowrap',
        ...(labelColumnMinWidth != null ? {minWidth: labelColumnMinWidth, width: labelColumnMinWidth} : {}),
    } as const;

    const formatControlValue = (diff: number, variancePerc: number) => {
        if (controlDisplay === 'variance') {
            return `${variancePerc.toFixed(2)}%`;
        }
        return formatAmount(diff, showCents);
    };

    return (
        <Box sx={payload.sx}>
            <Table size="small" sx={{tableLayout: 'auto', width: 'max-content', maxWidth: '100%'}}>
                <TableBody>
                    <TableRow>
                        <TableCell sx={labelCellSx}/>
                        <TableCell align="center" sx={{whiteSpace: 'nowrap'}}>
                            <Typography variant="body2" sx={{fontWeight: 600}} noWrap>
                                {payload.yoa}
                            </Typography>
                        </TableCell>
                        {!ignoreLastYear && (
                            <TableCell align="center" sx={{whiteSpace: 'nowrap'}}>
                                <Typography variant="body2" sx={{fontWeight: 600}} noWrap>
                                    {payload.lastYoa}
                                </Typography>
                            </TableCell>
                        )}
                    </TableRow>

                    {amountRows && amountRows.length > 0 ? (
                        amountRows.map((row) => {
                            const rowLastYearEditable =
                                !ignoreLastYear && (row.lastYearEditable ?? lastYearEditable);
                            const hasLastYearSplit = row.lastYearCalculated != null;
                            const calculated = Number(row.lastYearCalculated) || 0;
                            // null = follow calculated (display); 0 is a real override → Split.
                            const hasStored = row.lastYearValue != null;
                            const stored = hasStored ? Number(row.lastYearValue) : 0;
                            const lastYearEqual = hasStored
                                && Math.abs(stored - calculated) < lastYearEqualEps;
                            // Display: null OR equals calculated. Split: set and differs (incl. 0), or Edit forced.
                            const splitActive =
                                hasLastYearSplit
                                && (
                                    (hasStored && !lastYearEqual)
                                    || !!forcedLastYearSplitKeys[row.key]
                                );
                            return (
                                <TableRow key={row.key}>
                                    <TableCell sx={labelCellSx}>
                                        <Typography variant="body2" sx={{fontWeight: 600}} noWrap>
                                            {row.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={amountCellSx}>{formatAmount(row.currentValue, showCents)}</TableCell>
                                    {!ignoreLastYear && (
                                        <TableCell sx={lastYearAmountCellSx}>
                                            {hasLastYearSplit ? (
                                                <MoneyField
                                                    key={`ly-split-${row.key}-${splitActive ? 'on' : 'off'}`}
                                                    value={calculated}
                                                    tiny
                                                    compact
                                                    align="center"
                                                    showCents={showCents}
                                                    sx={lastYearMoneyFieldSx}
                                                    separator="→"
                                                    suffixValue={splitActive ? stored : 0}
                                                    forceShowSplitMode={splitActive}
                                                    suffixReadOnly={!rowLastYearEditable}
                                                    icon={rowLastYearEditable
                                                        ? <EditIcon fontSize="small"/>
                                                        : undefined}
                                                    cancelIcon={rowLastYearEditable
                                                        ? <CloseIcon fontSize="small"/>
                                                        : undefined}
                                                    onEditStart={rowLastYearEditable
                                                        ? () => {
                                                            setForcedLastYearSplitKeys((prev) => ({
                                                                ...prev,
                                                                [row.key]: true,
                                                            }));
                                                            // From display (null or equal): seed right pane to calculated.
                                                            if (!hasStored || lastYearEqual) {
                                                                onAmountLastYearChange?.(row.key, calculated);
                                                            }
                                                        }
                                                        : undefined}
                                                    onSuffixChange={rowLastYearEditable
                                                        ? (n) => {
                                                            const v = Number(n);
                                                            onAmountLastYearChange?.(
                                                                row.key,
                                                                Number.isFinite(v) ? v : 0,
                                                            );
                                                        }
                                                        : undefined}
                                                    onEditCancel={rowLastYearEditable
                                                        ? () => {
                                                            setForcedLastYearSplitKeys((prev) => ({
                                                                ...prev,
                                                                [row.key]: false,
                                                            }));
                                                            onAmountLastYearEditCancel?.(row.key);
                                                        }
                                                        : undefined}
                                                />
                                            ) : rowLastYearEditable ? (
                                                <MoneyField
                                                    value={row.lastYearValue ?? 0}
                                                    editable
                                                    tiny
                                                    align="center"
                                                    showCents={showCents}
                                                    sx={lastYearMoneyFieldSx}
                                                    onChangeNumber={(n) =>
                                                        onAmountLastYearChange?.(row.key, Number(n) || 0)
                                                    }
                                                />
                                            ) : (
                                                formatAmount(row.lastYearValue ?? 0, showCents)
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell sx={labelCellSx}>
                                <Typography variant="body2" sx={{fontWeight: 600}} noWrap>
                                    {displayPerName}
                                </Typography>
                            </TableCell>
                            <TableCell sx={amountCellSx}>{formatAmount(payload.currentValue, showCents)}</TableCell>
                            {!ignoreLastYear && (
                                <TableCell sx={lastYearAmountCellSx}>
                                    {lastYearEditable ? (
                                        <MoneyField
                                            value={payload.lastYearValue}
                                            editable
                                            tiny
                                            align="center"
                                            showCents={showCents}
                                            sx={lastYearMoneyFieldSx}
                                            onChangeNumber={(n) => onLastYearChange?.(Number(n) || 0)}
                                        />
                                    ) : (
                                        formatAmount(payload.lastYearValue, showCents)
                                    )}
                                </TableCell>
                            )}
                        </TableRow>
                    )}

                    {!amountsOnly && (
                        <TableRow>
                            <TableCell sx={labelCellSx}>
                                <Typography variant="body2" sx={{fontWeight: 600}} noWrap>
                                    Per {statement}
                                </Typography>
                            </TableCell>
                            <TableCell sx={amountCellSx}>{formatAmount(effectiveAuditedValue, showCents)}</TableCell>
                            {!ignoreLastYear && (
                                <TableCell sx={amountCellSx}>{formatAmount(effectiveLastYearAudited, showCents)}</TableCell>
                            )}
                        </TableRow>
                    )}

                    {!amountsOnly && (
                        <TableRow>
                            <TableCell sx={labelCellSx}>
                                <Typography variant="body2" sx={{fontWeight: 600}} noWrap>
                                    {controlDisplay === 'variance' ? 'Variance %' : 'Control'}
                                </Typography>
                            </TableCell>
                            <TableCell sx={diffCellSx(currentOk)}>
                                {formatControlValue(currentDiff, currentVariancePerc)}
                            </TableCell>
                            {!ignoreLastYear && (
                                <TableCell sx={diffCellSx(lastYearOk)}>
                                    {formatControlValue(lastYearDiff, lastYearVariancePerc)}
                                </TableCell>
                            )}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
}
